import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { useApi } from '../hooks/useApi';
import { isMultiTenantMode } from '../config/api';
import axios from 'axios';

const HealthContext = createContext();

export const useHealth = () => {
  const context = useContext(HealthContext);
  if (!context) {
    throw new Error('useHealth must be used within a HealthProvider');
  }
  return context;
};

export const HealthProvider = ({ children }) => {
  const { selectedEnvironment } = useAuth();
  const { checkHealthSimple, checkHealthDetailed } = useApi();
  
  // State voor current environment health (single tenant of selected environment)
  const [currentEnvironmentHealth, setCurrentEnvironmentHealth] = useState({
    isHealthy: null,
    status: 'checking',
    lastCheck: null,
    data: null,
    error: null
  });
  
  // State voor alle environments health (multi tenant)
  const [allEnvironmentsHealth, setAllEnvironmentsHealth] = useState({});
  
  // State voor environments lijst (multi tenant)
  const [environments, setEnvironments] = useState([]);
  
  // State om bij te houden of all environments monitoring actief moet zijn
  const [isAllEnvironmentsMonitoringActive, setIsAllEnvironmentsMonitoringActive] = useState(false);
  
  const [isChecking, setIsChecking] = useState(false);
  const [failureCounts, setFailureCounts] = useState({});
  
  // Refs voor interval management
  const currentEnvIntervalRef = useRef(null);
  const allEnvsIntervalRef = useRef(null);
  
  // Refs voor stable access to latest values
  const selectedEnvironmentRef = useRef(selectedEnvironment);
  const failureCountsRef = useRef(failureCounts);
  const allEnvironmentsHealthRef = useRef(allEnvironmentsHealth);
  const environmentsRef = useRef(environments);
  const isCheckingRef = useRef(isChecking);
  
  // Update refs when state changes
  useEffect(() => {
    selectedEnvironmentRef.current = selectedEnvironment;
  }, [selectedEnvironment]);
  
  useEffect(() => {
    failureCountsRef.current = failureCounts;
  }, [failureCounts]);
  
  useEffect(() => {
    allEnvironmentsHealthRef.current = allEnvironmentsHealth;
  }, [allEnvironmentsHealth]);
  
  useEffect(() => {
    environmentsRef.current = environments;
  }, [environments]);
  
  useEffect(() => {
    isCheckingRef.current = isChecking;
  }, [isChecking]);
  
  // Configuration
  const CURRENT_ENV_INTERVAL = 8000; // 8 seconden voor huidige environment (sneller door simple check)
  const ALL_ENVS_INTERVAL = 8000; // 8 seconden voor alle environments (sneller door simple check)
  const MAX_RETRIES = 2;
  const RETRY_DELAY = 1500; // Snellere retry door snellere checks
  const REQUEST_TIMEOUT = 8000; // Lagere timeout voor simple checks
  
  // Stable function voor current environment health check
  const checkCurrentEnvironmentHealthStable = useCallback(async () => {
    const currentSelectedEnv = selectedEnvironmentRef.current;
    const currentIsChecking = isCheckingRef.current;
    
    if (!currentSelectedEnv || currentIsChecking) return;
    
    setIsChecking(true);
    try {
      // Gebruik snelle health check voor regelmatige controles
      const result = await checkHealthSimple();
      setCurrentEnvironmentHealth({
        isHealthy: result.isHealthy,
        status: result.status,
        lastCheck: new Date(),
        data: result.data,
        error: result.error || null
      });
      
      // Reset failure count on success
      if (result.isHealthy) {
        setFailureCounts(prev => ({
          ...prev,
          [currentSelectedEnv.id]: 0
        }));
      }
      
    } catch (error) {
      const envId = currentSelectedEnv.id;
      setFailureCounts(prev => {
        const currentFailures = prev[envId] || 0;
        const newFailures = currentFailures + 1;
        
        // Alleen offline markeren na 3 opeenvolgende fouten
        if (newFailures >= 3) {
          setCurrentEnvironmentHealth(prevHealth => ({
            ...prevHealth,
            isHealthy: false,
            status: 'error',
            lastCheck: new Date(),
            error: error.message
          }));
        } else {
          // Warning state
          setCurrentEnvironmentHealth(prevHealth => ({
            ...prevHealth,
            warning: `Verbindingsproblemen (${newFailures}/3)`,
            lastCheck: new Date()
          }));
        }
        
        return {
          ...prev,
          [envId]: newFailures
        };
      });
    } finally {
      setIsChecking(false);
    }
  }, [checkHealthSimple]);
  
  // Helper functie om useApi te gebruiken voor specifieke environment
  const createEnvironmentApiClient = useCallback((environment) => {
    const { getBaseURL, getApiHeaders, isProxyModeEnabled } = require('../config/api');
    
    // Tijdelijk de environment selecteren voor deze API call
    const originalEnvironment = localStorage.getItem('selected_environment');
    localStorage.setItem('selected_environment', environment.id);
    localStorage.setItem(`environment_${environment.id}`, JSON.stringify(environment));
    
    try {
      const baseURL = getBaseURL();
      const headers = getApiHeaders();
      
      console.debug(`🔗 Environment health check: Using base URL: ${baseURL} for environment ${environment.name}`);
      
      const apiClient = axios.create({
        baseURL,
        headers,
        timeout: REQUEST_TIMEOUT
      });
      
      return apiClient;
    } finally {
      // Herstel originele environment selectie
      if (originalEnvironment) {
        localStorage.setItem('selected_environment', originalEnvironment);
      } else {
        localStorage.removeItem('selected_environment');
      }
    }
  }, []);

  // Check environment health with retry (stable version) - uses simple check for monitoring
  const checkEnvironmentHealthWithRetryStable = useCallback(async (environment, maxRetries = MAX_RETRIES, retryDelay = RETRY_DELAY) => {
    let lastError;
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const apiClient = createEnvironmentApiClient(environment);
        const response = await apiClient.get('/health/simple');
        
        return {
          environmentId: environment.id,
          success: true,
          isHealthy: response.data.status === 'healthy',
          status: response.data.status,
          data: response.data,
          lastCheck: new Date(),
          consecutiveFailures: 0
        };
        
      } catch (error) {
        lastError = error;
        console.warn(`Health check attempt ${attempt}/${maxRetries} failed for ${environment.name}:`, error.message);
        
        // Check if this is a critical error that shouldn't be retried
        const isCriticalError = error.message && (
          error.message.includes('ERR_NAME_NOT_RESOLVED') ||
          error.message.includes('Network Error') ||
          error.message.includes('ENOTFOUND') ||
          error.message.includes('ECONNREFUSED')
        );
        
        // Stop retrying immediately for critical errors
        if (isCriticalError) {
          console.warn(`Critical error detected for ${environment.name}, stopping retries:`, error.message);
          break;
        }
        
        if (attempt < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, retryDelay));
        }
      }
    }
    
    console.error(`All ${maxRetries} health check attempts failed for environment ${environment.name}:`, lastError.message);
    
    const currentFailureCounts = failureCountsRef.current;
    return {
      environmentId: environment.id,
      success: false,
      isHealthy: false,
      status: 'unreachable',
      error: lastError.message,
      lastCheck: new Date(),
      consecutiveFailures: (currentFailureCounts[environment.id] || 0) + 1
    };
  }, [createEnvironmentApiClient]);
  
  // Check environment health with detailed info (only when details needed)
  const checkEnvironmentHealthDetailed = useCallback(async (environment, maxRetries = MAX_RETRIES, retryDelay = RETRY_DELAY) => {
    let lastError;
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const apiClient = createEnvironmentApiClient(environment);
        const response = await apiClient.get('/health');
        
        return {
          environmentId: environment.id,
          success: true,
          isHealthy: response.data.status === 'healthy',
          status: response.data.status,
          data: response.data,
          lastCheck: new Date(),
          consecutiveFailures: 0,
          hasDetailedInfo: true
        };
        
      } catch (error) {
        lastError = error;
        console.warn(`Detailed health check attempt ${attempt}/${maxRetries} failed for ${environment.name}:`, error.message);
        
        if (attempt < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, retryDelay));
        }
      }
    }
    
    console.error(`All ${maxRetries} detailed health check attempts failed for environment ${environment.name}:`, lastError.message);
    
    const currentFailureCounts = failureCountsRef.current;
    return {
      environmentId: environment.id,
      success: false,
      isHealthy: false,
      status: 'unreachable',
      error: lastError.message,
      lastCheck: new Date(),
      consecutiveFailures: (currentFailureCounts[environment.id] || 0) + 1
    };
  }, [createEnvironmentApiClient]);
  
  // Check all environments health (stable version)
  const checkAllEnvironmentsHealthStable = useCallback(async () => {
    const currentEnvironments = environmentsRef.current;
    const currentIsChecking = isCheckingRef.current;
    const currentAllEnvironmentsHealth = allEnvironmentsHealthRef.current;
    const currentFailureCounts = failureCountsRef.current;
    
    if (!isMultiTenantMode() || currentEnvironments.length === 0 || currentIsChecking) return;
    
    setIsChecking(true);
    try {
      const healthPromises = currentEnvironments.map(env => checkEnvironmentHealthWithRetryStable(env, MAX_RETRIES, RETRY_DELAY));
      const results = await Promise.allSettled(healthPromises);
      
      const newHealthStatuses = {};
      const newFailureCounts = { ...currentFailureCounts };
      
      results.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          const healthData = result.value;
          const envId = healthData.environmentId;
          
          if (healthData.success) {
            // Success - reset failure count
            newFailureCounts[envId] = 0;
            newHealthStatuses[envId] = healthData;
          } else {
            // Failure - increment count
            const currentFailures = newFailureCounts[envId] || 0;
            const consecutiveFailures = currentFailures + 1;
            newFailureCounts[envId] = consecutiveFailures;
            
            // Check if this is a critical error (network unreachable)
            const isCriticalError = healthData.error && (
              healthData.error.includes('ERR_NAME_NOT_RESOLVED') ||
              healthData.error.includes('Network Error') ||
              healthData.error.includes('ENOTFOUND') ||
              healthData.error.includes('ECONNREFUSED')
            );
            
            // Mark as offline immediately for critical errors, or after 3 consecutive failures for other errors
            if (isCriticalError || consecutiveFailures >= 3 || !currentAllEnvironmentsHealth[envId] || !currentAllEnvironmentsHealth[envId].isHealthy) {
              // Mark as offline
              newHealthStatuses[envId] = {
                ...healthData,
                consecutiveFailures
              };
            } else {
              // Keep previous healthy status but add warning
              newHealthStatuses[envId] = {
                ...currentAllEnvironmentsHealth[envId],
                warning: `Verbindingsproblemen (${consecutiveFailures}/3)`,
                lastCheck: new Date(),
                consecutiveFailures
              };
            }
          }
        } else {
          // Promise rejected - fallback
          const env = currentEnvironments[index];
          newHealthStatuses[env.id] = {
            environmentId: env.id,
            isHealthy: false,
            status: 'error',
            error: result.reason?.message || 'Unknown error',
            lastCheck: new Date(),
            consecutiveFailures: (newFailureCounts[env.id] || 0) + 1
          };
          newFailureCounts[env.id] = (newFailureCounts[env.id] || 0) + 1;
        }
      });
      
      setFailureCounts(newFailureCounts);
      setAllEnvironmentsHealth(newHealthStatuses);
      
    } catch (error) {
      console.error('Error checking environments health:', error);
    } finally {
      setIsChecking(false);
    }
  }, [checkEnvironmentHealthWithRetryStable]);
  
  // Functie voor gedetailleerde health check (wanneer extra info nodig is)
  const checkCurrentEnvironmentHealthDetailed = useCallback(async () => {
    const currentSelectedEnv = selectedEnvironmentRef.current;
    const currentIsChecking = isCheckingRef.current;
    
    if (!currentSelectedEnv || currentIsChecking) return null;
    
    setIsChecking(true);
    try {
      // Gebruik gedetailleerde health check voor extra informatie
      const result = await checkHealthDetailed();
      
      // Update de state met gedetailleerde informatie
      setCurrentEnvironmentHealth(prev => ({
        ...prev,
        detailedData: result.data,
        lastDetailedCheck: new Date()
      }));
      
      return result;
      
    } catch (error) {
      console.error('Detailed health check failed:', error);
      return {
        isHealthy: false,
        status: 'error',
        error: error.message
      };
    } finally {
      setIsChecking(false);
    }
  }, [checkHealthDetailed]);

  // Helper functies
  const getCurrentEnvironmentHealth = useCallback(() => {
    return currentEnvironmentHealth;
  }, [currentEnvironmentHealth]);
  
  const getEnvironmentHealth = useCallback((environmentId) => {
    return allEnvironmentsHealth[environmentId] || {
      environmentId,
      isHealthy: null,
      status: 'checking',
      lastCheck: null,
      data: null
    };
  }, [allEnvironmentsHealth]);
  
  const getHealthDisplay = useCallback((environmentId) => {
    const health = getEnvironmentHealth(environmentId);
    
    if (health.isHealthy === null) {
      return { status: 'checking', label: 'Controleren...', color: 'warning' };
    }
    
    // Check for warning state (healthy but with connection issues)
    if (health.warning && health.isHealthy) {
      return { status: 'warning', label: 'Beperkt', color: 'warning' };
    }
    
    switch (health.status) {
      case 'healthy':
        return { status: 'healthy', label: 'Online', color: 'success' };
      case 'degraded':
        return { status: 'degraded', label: 'Beperkt', color: 'warning' };
      case 'unhealthy':
        return { status: 'unhealthy', label: 'Offline', color: 'error' };
      default:
        return { status: 'unreachable', label: 'Onbereikbaar', color: 'error' };
    }
  }, [getEnvironmentHealth]);
  
  const getCurrentEnvironmentDisplay = useCallback(() => {
    if (!selectedEnvironment) {
      return { status: 'unknown', label: 'Geen omgeving', color: 'grey' };
    }
    
    if (currentEnvironmentHealth.isHealthy === null) {
      return { status: 'checking', label: 'Controleren...', color: 'warning' };
    }
    
    // Check for warning state
    if (currentEnvironmentHealth.warning && currentEnvironmentHealth.isHealthy) {
      return { status: 'warning', label: 'Beperkt', color: 'warning' };
    }
    
    switch (currentEnvironmentHealth.status) {
      case 'healthy':
        return { status: 'healthy', label: 'Online', color: 'success.main' };
      case 'degraded':
        return { status: 'degraded', label: 'Beperkt', color: 'warning.main' };
      case 'unhealthy':
        return { status: 'unhealthy', label: 'Offline', color: 'error.main' };
      default:
        return { status: 'unreachable', label: 'Onbereikbaar', color: 'error.main' };
    }
  }, [selectedEnvironment, currentEnvironmentHealth]);
  
  // Manual refresh functies
  const refreshCurrentEnvironment = useCallback(() => {
    checkCurrentEnvironmentHealthStable();
  }, [checkCurrentEnvironmentHealthStable]);
  
  const refreshAllEnvironments = useCallback(() => {
    checkAllEnvironmentsHealthStable();
  }, [checkAllEnvironmentsHealthStable]);
  
  // Functie om gedetailleerde health info op te halen voor een specifiek environment
  const getDetailedEnvironmentHealth = useCallback(async (environmentId) => {
    const currentEnvironments = environmentsRef.current;
    const environment = currentEnvironments.find(env => env.id === environmentId);
    
    if (!environment) {
      console.error(`Environment ${environmentId} not found`);
      return null;
    }
    
    try {
      const detailedHealth = await checkEnvironmentHealthDetailed(environment);
      
      // Update de state met gedetailleerde informatie
      setAllEnvironmentsHealth(prev => ({
        ...prev,
        [environmentId]: detailedHealth
      }));
      
      return detailedHealth;
    } catch (error) {
      console.error(`Failed to get detailed health for environment ${environmentId}:`, error);
      return null;
    }
  }, [checkEnvironmentHealthDetailed]);
  
  // Functies om all environments monitoring aan/uit te zetten
  const startAllEnvironmentsMonitoring = useCallback(() => {
    setIsAllEnvironmentsMonitoringActive(true);
  }, []);
  
  const stopAllEnvironmentsMonitoring = useCallback(() => {
    setIsAllEnvironmentsMonitoringActive(false);
  }, []);
  
  // Effect voor current environment monitoring
  useEffect(() => {
    // Clear existing interval
    if (currentEnvIntervalRef.current) {
      clearInterval(currentEnvIntervalRef.current);
      currentEnvIntervalRef.current = null;
    }
    
    if (!selectedEnvironment) {
      return;
    }
    
    // Eerste check direct uitvoeren
    checkCurrentEnvironmentHealthStable();
    
    // Start nieuwe interval
    currentEnvIntervalRef.current = setInterval(() => {
      checkCurrentEnvironmentHealthStable();
    }, CURRENT_ENV_INTERVAL);
    
    return () => {
      if (currentEnvIntervalRef.current) {
        clearInterval(currentEnvIntervalRef.current);
        currentEnvIntervalRef.current = null;
      }
    };
  }, [selectedEnvironment]); // Alleen selectedEnvironment als dependency!
  
  // Effect voor all environments monitoring (alleen wanneer expliciet geactiveerd)
  useEffect(() => {
    // Clear existing interval
    if (allEnvsIntervalRef.current) {
      clearInterval(allEnvsIntervalRef.current);
      allEnvsIntervalRef.current = null;
    }
    
    if (!isMultiTenantMode() || environments.length === 0 || !isAllEnvironmentsMonitoringActive) {
      return;
    }
    
    // Eerste check direct uitvoeren
    checkAllEnvironmentsHealthStable();
    
    // Start nieuwe interval
    allEnvsIntervalRef.current = setInterval(() => {
      checkAllEnvironmentsHealthStable();
    }, ALL_ENVS_INTERVAL);
    
    return () => {
      if (allEnvsIntervalRef.current) {
        clearInterval(allEnvsIntervalRef.current);
        allEnvsIntervalRef.current = null;
      }
    };
  }, [environments.length, isAllEnvironmentsMonitoringActive]); // Alleen wanneer monitoring geactiveerd is!
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (currentEnvIntervalRef.current) {
        clearInterval(currentEnvIntervalRef.current);
      }
      if (allEnvsIntervalRef.current) {
        clearInterval(allEnvsIntervalRef.current);
      }
    };
  }, []);
  
  const value = {
    // Current environment (voor sidebar, dashboard etc.)
    currentEnvironmentHealth,
    getCurrentEnvironmentHealth,
    getCurrentEnvironmentDisplay,
    refreshCurrentEnvironment,
    checkCurrentEnvironmentHealthDetailed, // Voor gedetailleerde health info
    
    // All environments (voor management pages)
    allEnvironmentsHealth,
    getEnvironmentHealth,
    getHealthDisplay,
    refreshAllEnvironments,
    getDetailedEnvironmentHealth, // Voor gedetailleerde info van specifiek environment
    startAllEnvironmentsMonitoring,
    stopAllEnvironmentsMonitoring,
    
    // Shared state
    isChecking,
    
    // Configuration voor external gebruik
    setEnvironments,
    environments
  };
  
  return (
    <HealthContext.Provider value={value}>
      {children}
    </HealthContext.Provider>
  );
}; 